import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { EtiquetaTentativa } from './FechaTentativa';
import FichaVisitaTecnico from './FichaVisitaTecnico';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { LuInbox, LuPin } from 'react-icons/lu';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { formatDateISO, lunesAgenda } from '../../utils/dateUtils';
import DireccionMapa from '../ui/DireccionMapa';

const inputCls = 'w-full px-3 py-2.5 rounded-xl bg-chip text-ink text-body font-medium outline-none focus:ring-2 focus:ring-[#D13A28]/40 placeholder:text-muted';
const labelCls = 'block text-label font-black text-muted uppercase tracking-wider mb-1';

// Generar dias de la semana (lunes a sabado) — misma cuenta de "lunes de la
// semana" que usa WeekDatePicker.jsx, centralizada en dateUtils.js.
function generarSemana(offset = 0) {
    const lunes = lunesAgenda();
    lunes.setDate(lunes.getDate() + offset * 7);
    const dias = [];
    // Una semana lun–sáb (5-oct-2026: dos semanas era mucho)
    for (let i = 0; i < 6; i++) {
        const d = new Date(lunes);
        d.setDate(lunes.getDate() + i);
        dias.push({
            fecha: formatDateISO(d),
            dia: d,
            esHoy: formatDateISO(d) === formatDateISO(new Date()),
        });
    }
    return dias;
}

function DiaBtn({ d, seleccionado, count, onClick }) {
    return (
        <button onClick={onClick}
            className={`rounded-lg p-2 text-center transition-all active:scale-95 shadow-sm border border-black/[0.05] dark:border-white/[0.05] ${
                d.esHoy ? 'ring-2 ring-brand-red' : ''
            } ${seleccionado ? 'bg-ink' : 'bg-card'}`}>
            <p className={`text-label font-bold uppercase ${seleccionado ? 'text-white dark:text-[#1C1917]' : 'text-muted'}`}>
                {d.dia.toLocaleDateString('es-AR', { weekday: 'short' }).replace('.', '')}
            </p>
            <p className={`text-body-lg font-black ${seleccionado ? 'text-white dark:text-[#1C1917]' : 'text-ink'}`}>
                {d.dia.getDate()}
            </p>
            {count > 0 && (
                <div className={`mx-auto mt-1 w-5 h-5 rounded-full flex items-center justify-center text-label font-black ${
                    seleccionado ? 'bg-[#D13A28] text-white' : 'bg-panel text-ink'
                }`}>{count}</div>
            )}
        </button>
    );
}

// Card de orden asignada (del admin)
function OrdenAgendaCard({ orden, onConfirmar, onAbrir }) {
    const hoyISO = formatDateISO(new Date());
    const tentativa = !!orden.sinConfirmar && orden.estado === 'PENDIENTE';
    const puedeConfirmar = orden.estado === 'PENDIENTE' && !tentativa && !orden.confirmadaEn && onConfirmar
        && (!orden.fechaProgramada || orden.fechaProgramada >= hoyISO);
    const borderColor = {
        PENDIENTE: '#A8A29E', EN_CAMINO: '#3B82F6', EN_SITIO: '#D48800',
        COMPLETADA: '#16A34A', NO_ATENDIDO: '#DC2626',
    }[orden.estado] || '#A8A29E';

    return (
        <div role="button" tabIndex={0} onClick={() => onAbrir(orden)} onKeyDown={e => { if (e.key === 'Enter') onAbrir(orden); }}
            className="cursor-pointer active:scale-[0.99] transition-transform rounded-xl bg-card shadow-sm border border-black/[0.05] dark:border-white/[0.05] px-3.5 py-2.5 border-l-[3px]"
            style={{ borderLeftColor: borderColor }}>
            <div className="flex items-center gap-2">
                <LuPin size={18} />
                <div className="flex-1 min-w-0">
                    <p className="text-body font-bold text-ink truncate">{orden.titulo}</p>
                    {orden.horaEstimada && (
                        <p className="text-caption text-muted">{orden.horaEstimada}</p>
                    )}
                </div>
                {tentativa ? <EtiquetaTentativa className="shrink-0" /> : (
                <span className="text-label font-bold px-1.5 py-0.5 rounded shrink-0" style={{ color: borderColor, backgroundColor: borderColor + '15' }}>
                    {orden.estado === 'PENDIENTE' ? (orden.confirmadaEn ? '✓ CONFIRMADA' : 'ASIGNADA') : orden.estado?.replace('_', ' ')}
                </span>)}
            </div>
            {orden.direccion && (
                // 31-oct-2026: toda la tarjeta abre la visita (antes el renglón de la dirección no respondía); el botón Mapa no la abre
                <DireccionMapa direccion={orden.direccion} className="mt-1" />
            )}
            {/* "Ok, voy" también desde la agenda (5-oct-2026): antes solo estaba en "Hoy" */}
            {puedeConfirmar && (
                <button onClick={e => { e.stopPropagation(); onConfirmar(orden); }}
                    className="mt-2 w-full py-2 rounded-xl font-black text-body text-ink bg-card border-2 border-[color:var(--etapa-listo)] active:scale-95 transition-all">
                    ✓ Ok, voy
                </button>
            )}
            {['PENDIENTE', 'EN_CAMINO', 'EN_SITIO'].includes(orden.estado) && !tentativa && (
                <p className="mt-1.5 text-caption text-muted">{orden.fechaProgramada && orden.fechaProgramada > hoyISO ? 'Tocala para ver el detalle.' : 'Tocala para ver el paso a paso y avanzarla.'}</p>
            )}
        </div>
    );
}

// Card de nota personal
function NotaCard({ nota, onToggle, onEliminar }) {
    const [confirmElim, setConfirmElim] = useState(false);

    return (
        <div className={`rounded-xl shadow-sm border border-black/[0.05] dark:border-white/[0.05] px-3.5 py-2.5 border-l-[3px] transition-opacity ${
            nota.completada ? 'opacity-50 bg-panel' : 'bg-card'
        }`} style={{ borderLeftColor: nota.completada ? '#16A34A' : '#D48800' }}>
            <div className="flex items-center gap-2">
                <button onClick={() => onToggle(nota.id)}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 active:scale-90 transition-all ${
                        nota.completada
                            ? 'bg-[#16A34A] border-[#16A34A] text-white'
                            : 'border-muted text-transparent'
                    }`}>
                    {nota.completada && <span className="text-label">✓</span>}
                </button>
                <div className="flex-1 min-w-0">
                    <p className={`text-body font-bold truncate ${nota.completada ? 'line-through text-muted' : 'text-ink'}`}>
                        {nota.titulo}
                    </p>
                    {nota.horaEstimada && (
                        <p className="text-caption text-muted">{nota.horaEstimada}</p>
                    )}
                </div>
                {!confirmElim ? (
                    <button onClick={() => setConfirmElim(true)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-label text-muted active:scale-90">
                        ×
                    </button>
                ) : (
                    <button onClick={() => { setConfirmElim(false); onEliminar(nota.id); }}
                        className="h-6 px-2 rounded-lg text-label font-black uppercase bg-[#DC2626] text-white active:scale-90">
                        Borrar
                    </button>
                )}
            </div>
            {nota.descripcion && (
                <p className="text-caption text-muted mt-1 line-clamp-2">{nota.descripcion}</p>
            )}
            {nota.direccion && (
                <DireccionMapa direccion={nota.direccion} className="mt-1" />
            )}
        </div>
    );
}

// Sheet para crear nota
function CrearNotaSheet({ fecha, tecnicoId, onCreada, onCerrar }) {
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [direccion, setDireccion] = useState('');
    const [hora, setHora] = useState('');
    const [guardando, setGuardando] = useState(false);

    const handleGuardar = async () => {
        if (!titulo.trim()) { toast.error('Agrega un titulo'); return; }
        setGuardando(true);
        try {
            await api.post('/notas-agenda', {
                tecnicoId,
                fecha,
                horaEstimada: hora || null,
                titulo: titulo.trim(),
                descripcion: descripcion.trim() || null,
                direccion: direccion.trim() || null,
                completada: false,
            });
            toast.success('Nota agregada');
            onCreada();
        } catch {
            toast.error('Error al guardar');
        } finally {
            setGuardando(false);
        }
    };

    const fechaLabel = (() => {
        const d = new Date(fecha + 'T00:00:00');
        return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    })();

    return (
        <div className="fixed inset-0 z-[3000] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6"
            onClick={onCerrar}>
            <div className="w-full max-w-md bg-card rounded-t-3xl md:rounded-3xl shadow-2xl p-5 space-y-4"
                onClick={e => e.stopPropagation()}>
                <div className="w-10 h-1 rounded-full mx-auto bg-chip md:hidden" />
                <div>
                    <p className="text-label font-black text-muted uppercase tracking-widest mb-0.5">Nueva nota</p>
                    <p className="text-body font-bold text-ink capitalize">{fechaLabel}</p>
                </div>

                <div>
                    <label className={labelCls}>Titulo *</label>
                    <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
                        placeholder="Ej: Reparacion dispenser en oficina, Cambio filtros..."
                        className={inputCls} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Hora estimada</label>
                        <input type="time" value={hora} onChange={e => setHora(e.target.value)}
                            className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Direccion</label>
                        <input type="text" value={direccion} onChange={e => setDireccion(e.target.value)}
                            placeholder="Opcional" className={inputCls} />
                    </div>
                </div>

                <div>
                    <label className={labelCls}>Descripcion (opcional)</label>
                    <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)}
                        rows={2} placeholder="Detalles, que llevar, datos del contacto..."
                        className={`${inputCls} resize-none`} />
                </div>

                <div className="flex gap-2">
                    <button onClick={onCerrar}
                        className="flex-1 py-3 rounded-2xl font-black text-label uppercase bg-chip text-secondary active:scale-95 transition-all">
                        Cancelar
                    </button>
                    <button onClick={handleGuardar} disabled={guardando || !titulo.trim()}
                        className="flex-[2] py-3 rounded-2xl font-black text-label uppercase text-white bg-brand-red active:scale-95 transition-all disabled:opacity-40">
                        {guardando ? 'Guardando...' : 'Agregar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function MiAgenda({ tecnicoId, embebido = false }) {
    const [semanaOffset, setSemanaOffset] = useState(0);
    const diaHoy = (() => {
        const d = new Date();
        if (d.getDay() === 0) d.setDate(d.getDate() + 1); // domingo → lunes (la grilla es lun–sáb)
        return formatDateISO(d);
    })();
    const [diaSel, setDiaSel] = useState(diaHoy);
    const enHoy = semanaOffset === 0 && diaSel === diaHoy;
    // M13: al pasar de semana, el día elegido se mueve al mismo día de la semana nueva
    // (antes quedaba marcado el de la semana anterior y la lista no coincidía con la grilla)
    const offsetPrevio = useRef(0);
    useEffect(() => {
        const delta = semanaOffset - offsetPrevio.current;
        offsetPrevio.current = semanaOffset;
        if (!delta) return;
        setDiaSel(d => { const x = new Date(d + 'T12:00:00'); x.setDate(x.getDate() + delta * 7); return formatDateISO(x); });
    }, [semanaOffset]);
    const volverHoy = () => { offsetPrevio.current = 0; setSemanaOffset(0); setDiaSel(diaHoy); };
    // Deslizar sobre la semana: semana anterior / siguiente
    const swipeSemana = useSwipeGesture(['-1', '0', '1'], '0', id => setSemanaOffset(o => o + Number(id)));
    const [ordenes, setOrdenes] = useState([]);
    const [notas, setNotas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [creandoNota, setCreandoNota] = useState(false);
    const [ficha, setFicha] = useState(null); // visita abierta (paso a paso)

    const semana = useMemo(() => generarSemana(semanaOffset), [semanaOffset]);
    const desde = semana[0].fecha;
    const hasta = semana[semana.length - 1].fecha;

    const cargar = useCallback(async () => {
        if (!tecnicoId) return;
        setCargando(true);
        try {
            const [resOrdenes, resNotas] = await Promise.all([
                api.get(`/ordenes/historial/${tecnicoId}`),
                api.get(`/notas-agenda/${tecnicoId}`, { params: { desde, hasta } }),
            ]);
            // Filtrar ordenes de esta semana
            const todas = resOrdenes.data || [];
            // Las canceladas (pausadas o devueltas) no se muestran en la agenda (5-oct-2026)
            const enSemana = todas.filter(o => o.fechaProgramada >= desde && o.fechaProgramada <= hasta && o.estado !== 'CANCELADA');
            setOrdenes(enSemana);
            setNotas(resNotas.data || []);
        } catch {
            toast.error('Error al cargar agenda');
        } finally {
            setCargando(false);
        }
    }, [tecnicoId, desde, hasta]);

    useEffect(() => { cargar(); }, [cargar]);

    const confirmarVisita = async (o) => {
        try {
            await api.patch(`/ordenes/${o.id}/confirmar`);
            toast.success('Listo, el admin ya sabe que vas');
            cargar();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo confirmar'); }
    };

    const toggleNota = async (id) => {
        try {
            await api.patch(`/notas-agenda/${id}/toggle`);
            cargar();
        } catch { toast.error('Error al actualizar'); }
    };

    const eliminarNota = async (id) => {
        try {
            await api.delete(`/notas-agenda/${id}`);
            toast.success('Nota eliminada');
            cargar();
        } catch { toast.error('Error al eliminar'); }
    };

    // Items del dia seleccionado
    const ordenesDia = ordenes.filter(o => o.fechaProgramada === diaSel);
    const notasDia = notas.filter(n => n.fecha === diaSel);

    // Conteos por dia para los botones
    const conteosPorDia = useMemo(() => {
        const c = {};
        semana.forEach(d => {
            const oCount = ordenes.filter(o => o.fechaProgramada === d.fecha).length;
            const nCount = notas.filter(n => n.fecha === d.fecha).length;
            c[d.fecha] = oCount + nCount;
        });
        return c;
    }, [semana, ordenes, notas]);

    const fechaLabel = (() => {
        const d = new Date(diaSel + 'T00:00:00');
        return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    })();

    const mesLabel = (() => {
        const mid = semana[2]?.dia || new Date();
        return mid.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
    })();

    return (
        <>
        {/* embebido: se muestra como pestaña "Agenda" adentro de "Hoy" (MisOrdenes.jsx) */}
        <div className={embebido ? '' : 'min-h-screen pb-28 bg-page'}>
            <div className={embebido ? '' : 'max-w-2xl mx-auto px-4 pt-4'}>
                {/* Header */}
                <div className={`flex items-center justify-between mb-4 ${embebido ? 'hidden' : ''}`}>
                    <div>
                        <h1 className="text-title font-black text-ink">Mi Agenda</h1>
                        <p className="text-caption text-muted capitalize">{mesLabel}</p>
                    </div>
                    <div className="flex gap-1.5">
                        <button onClick={() => setSemanaOffset(v => v - 1)}
                            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold bg-card text-secondary shadow-sm border border-black/[0.05] dark:border-white/[0.05] active:scale-90">
                            ←
                        </button>
                        {!enHoy && (
                            <button onClick={volverHoy}
                                className="h-9 px-3 rounded-xl font-bold text-label bg-card text-brand-red shadow-sm border border-black/[0.05] dark:border-white/[0.05] active:scale-90">
                                Hoy
                            </button>
                        )}
                        <button onClick={() => setSemanaOffset(v => v + 1)}
                            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold bg-card text-secondary shadow-sm border border-black/[0.05] dark:border-white/[0.05] active:scale-90">
                            →
                        </button>
                    </div>
                </div>

                {/* Navegación compacta cuando va embebida (en "Lo mío") */}
                {embebido && (
                    <div className="flex items-center justify-between mb-2">
                        <button onClick={() => setSemanaOffset(v => v - 1)} aria-label="Semana anterior"
                            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold bg-card text-secondary border border-black/[0.05] dark:border-white/[0.05] active:scale-90">←</button>
                        <span className="flex items-center gap-2">
                            <span className="text-label font-black text-muted uppercase tracking-wider">
                                {semanaOffset === 0 ? 'Esta semana' : semanaOffset === 1 ? 'La que viene' : mesLabel}
                            </span>
                            {!enHoy && (
                                <button onClick={volverHoy}
                                    className="h-8 px-3 rounded-lg border border-brand-red text-label font-black text-ink active:scale-95">Hoy</button>
                            )}
                        </span>
                        <button onClick={() => setSemanaOffset(v => v + 1)} aria-label="Semana siguiente"
                            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold bg-card text-secondary border border-black/[0.05] dark:border-white/[0.05] active:scale-90">→</button>
                    </div>
                )}
                {/* Una semana */}
                <div className="space-y-1.5 mb-4" data-noswipe {...swipeSemana}>
                    {[semana].map((fila, i) => (
                        <div key={i} className="grid grid-cols-6 gap-1.5">
                            {fila.map(d => (
                                <DiaBtn key={d.fecha} d={d}
                                    seleccionado={d.fecha === diaSel}
                                    count={conteosPorDia[d.fecha] || 0}
                                    onClick={() => setDiaSel(d.fecha)} />
                            ))}
                        </div>
                    ))}
                </div>

                {/* Detalle del dia */}
                <div className="flex items-center justify-between mb-3">
                    <p className="text-body font-black text-ink capitalize">{fechaLabel}</p>
                    <button onClick={() => setCreandoNota(true)}
                        className="h-8 px-3 rounded-lg font-bold text-label text-white bg-brand-red active:scale-95 transition-all">
                        + Nota
                    </button>
                </div>

                {cargando ? (
                    <div className="space-y-2">
                        {[1, 2].map(i => <div key={i} className="h-16 rounded-xl animate-pulse bg-card" />)}
                    </div>
                ) : ordenesDia.length === 0 && notasDia.length === 0 ? (
                    <div className="text-center py-12 rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07]">
                        <LuInbox size={24} className="mb-2 text-muted inline-block" />
                        <p className="text-body font-bold text-muted">Dia libre</p>
                        <button onClick={() => setCreandoNota(true)}
                            className="mt-3 text-label font-bold text-brand-red px-4 py-2 rounded-xl border border-[#D13A28]/30 dark:border-[#E8422F]/30 active:scale-95 transition-all">
                            Agregar nota
                        </button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {/* Ordenes del admin primero */}
                        {ordenesDia.length > 0 && (
                            <>
                                <p className="text-label font-black text-brand-red uppercase tracking-wider px-1">Ordenes asignadas</p>
                                {ordenesDia.map(o => <OrdenAgendaCard key={`o-${o.id}`} orden={o} onConfirmar={confirmarVisita} onAbrir={setFicha} />)}
                            </>
                        )}

                        {/* Notas personales */}
                        {notasDia.length > 0 && (
                            <>
                                <p className="text-label font-black text-brand-amber uppercase tracking-wider px-1 mt-3">Mis notas</p>
                                {notasDia.map(n => (
                                    <NotaCard key={`n-${n.id}`} nota={n} onToggle={toggleNota} onEliminar={eliminarNota} />
                                ))}
                            </>
                        )}
                    </div>
                )}

                {/* 5-oct-2026: se sacó "Mi Espacio" del técnico. Sus notas van acá arriba
                    (privadas, por día); lo que tenga que saber el admin va por "Avisar al admin". */}
            </div>
        </div>

        {ficha && <FichaVisitaTecnico orden={ficha} onCerrar={() => setFicha(null)} onCambio={cargar} />}
        {creandoNota && (
            <CrearNotaSheet
                fecha={diaSel}
                tecnicoId={tecnicoId}
                onCreada={() => { setCreandoNota(false); cargar(); }}
                onCerrar={() => setCreandoNota(false)}
            />
        )}
        </>
    );
}
